// src/pages/Admin/AdminPriceLists.tsx

import React, { useState, ChangeEvent } from 'react';
import axios from 'axios';

import Header from "../../components/Header";
import Footer from "../../components/Footer";

// We can now use a real 'config.json' for the backend URL
const config = require('../../config/config.json');

// Define a type for the summary statistics for better type-safety
interface IProcessingSummary {
    sheets: number;
    brands: number;
    totalRows: number;
    updated: number;
    inserted: number;
    skipped: number;
}

function AdminPriceLists() {
    // State for the selected file type
    const [fileType, setFileType] = useState<string>('Price List');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState('');
    const [processingDetails, setProcessingDetails] = useState<string[]>([]);
    
    // State for the new summary object
    const [summary, setSummary] = useState<IProcessingSummary | null>(null);

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setMessage('Please select a file to upload.');
            return;
        }

        setUploading(true);
        setMessage('Uploading and processing file...');
        setProcessingDetails([]); // Clear previous logs
        setSummary(null); // Clear previous summary

        const formData = new FormData();
        // The backend now expects a single file named 'priceListFile'
        formData.append('priceListFile', selectedFile); 
        // We also send the selected fileType
        formData.append('fileType', fileType);

        try {
            const response = await axios.post(`${config.backend.url}/api/pricelists/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            setMessage(response.data.message);
            if (response.data.details) {
                setProcessingDetails(response.data.details);
            }
            if (response.data.summary) {
                setSummary(response.data.summary);
            }
        } catch (error: any) {
            let errorMessage = 'Error uploading file. Please try again.';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }
            setMessage(errorMessage);
            if (error.response?.data?.details) {
                setProcessingDetails(error.response.data.details);
            }
            if (error.response?.data?.summary) {
                setSummary(error.response.data.summary);
            }
            console.error('Error uploading file:', error);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="app-container">
            <Header />
            <div className="app-content">
                <section className="sectionPageContent">
                    <h1>ADMIN - UPLOAD PRICE LISTS</h1>
                    
                    {/* File Type Selection */}
                    <div className="file-type-selector">
                        <h4>1. Select File Type:</h4>
                        <div>
                            <input type="radio" id="priceList" name="fileType" value="Price List" checked={fileType === 'Price List'} onChange={(e) => setFileType(e.target.value)} />
                            <label htmlFor="priceList">Price List</label>
                        </div>
                        <div>
                            <input type="radio" id="priceListSimple" name="fileType" value="Price List Simplified" checked={fileType === 'Price List Simplified'} onChange={(e) => setFileType(e.target.value)} />
                            <label htmlFor="priceListSimple">Price List Simplified</label>
                        </div>
                        <div>
                            <input type="radio" id="exoClearance" name="fileType" value="Exo Skeleton Clearance" checked={fileType === 'Exo Skeleton Clearance'} onChange={(e) => setFileType(e.target.value)} />
                            <label htmlFor="exoClearance">Exo Skeleton Clearance</label>
                        </div>
                    </div>

                    {/* File Upload Input */}
                    <div className="upload-container">
                        <h4>2. Choose File to Upload:</h4>
                        <input type="file" onChange={handleFileChange} accept=".xlsx, .xls" />
                        <button onClick={handleUpload} disabled={uploading || !selectedFile}>
                            {uploading ? 'Processing...' : 'Upload and Process'}
                        </button>
                    </div>

                    {/* Results Section */}
                    <div className="results-container">
                        {message && <p><strong>Status: {message}</strong></p>}
                        
                        {/* Summary Stats */}
                        {summary && (
                            <div className="processing-summary">
                                <h4>Processing Summary:</h4>
                                <ul>
                                    <li>Sheets (Pages) Processed: <strong>{summary.sheets}</strong></li>
                                    <li>Brands (from Sheets): <strong>{summary.brands}</strong></li>
                                    <li>Total Rows Found: <strong>{summary.totalRows}</strong></li>
                                    <li>Products Updated: <strong style={{color: 'blue'}}>{summary.updated}</strong></li>
                                    <li>Products Inserted: <strong style={{color: 'green'}}>{summary.inserted}</strong></li>
                                    <li>Rows Skipped (Errors): <strong style={{color: 'red'}}>{summary.skipped}</strong></li>
                                </ul>
                            </div>
                        )}
                        
                        {/* Log Details in a TextArea */}
                        {processingDetails.length > 0 && (
                            <div className="processing-log">
                                <h4>Processing Details Log:</h4>
                                <textarea
                                    readOnly
                                    value={processingDetails.join('\n')}
                                    rows={15}
                                    style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px' }}
                                />
                            </div>
                        )}
                    </div>
                </section>
            </div>
            <Footer />
        </div>
    );
}

export default AdminPriceLists;